import { useContext, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, ChevronRight } from "lucide-react";

import { AdminContext } from "../AdminLayout";
import { ROUTE_PATHS } from "../../../../constants/routeConstants";
import hasPermission from "../../../../Routes/HasPermission";

export const SideBarItem = ({
  icon,
  text,
  active,
  alert,
  link,
  module,
  permission,
  noPermission = false,
  adminPermission = false,
  subItems = [],
}) => {
  const { expanded, setExpanded, setMobileOpen } = useContext(AdminContext);
  const handleParentClick = () => {
    if (!expanded) {
      setExpanded(true);
      setOpen(true);
      return;
    }

    setOpen((prev) => !prev);
  };
  const location = useLocation();

  const hasSubItems = subItems.length > 0;

  const isActive =
    active ||
    (link === ROUTE_PATHS.DASHBOARD
      ? location.pathname === link
      : link && location.pathname.startsWith(link));

  const isChildActive = subItems.some((item) =>
    location.pathname.startsWith(item.link)
  );

  const [open, setOpen] = useState(true);
  const visibleSubItems = subItems.filter(
    (item) =>
      hasPermission(item.module, item.permission) ||
      item.noPermission ||
      item.adminPermission
  );
  const canView =
    hasPermission(module, permission) ||
    noPermission ||
    adminPermission ||
    visibleSubItems.length > 0;

  if (!canView) return null;

  return (
    <li className="relative group rounded-md my-1 text-[13px]">
      {hasSubItems ? (
        <>
          <button
            onClick={handleParentClick}
            className={`
      flex items-center w-full py-2.5 rounded-lg
      ${expanded ? "gap-3 px-3" : "justify-center px-2"}
      transition-all duration-300

      ${isChildActive
                ? "border-l-[3px] border-orange-500 bg-orange-50 text-orange-700 dark:bg-orange-500/10 dark:text-orange-400"
                : "text-slate-600 hover:text-orange-600 hover:bg-orange-100 dark:text-orange-300 dark:hover:bg-slate-800"
              }
    `}
          >
            <span className="shrink-0">
              {icon}
            </span>

            {expanded && (
              <>
                <span className="flex-1 text-left text-[13px] font-medium truncate">
                  {text}
                </span>

                <ChevronDown
                  size={14}
                  className={`transition-transform duration-300 ${open ? "rotate-180" : ""
                    }`}
                />
              </>
            )}
          </button>

          <div
            className={`
                overflow-hidden transition-all duration-300 ease-in-out
                ${open && expanded
                ? "max-h-96 opacity-100"
                : "max-h-0 opacity-0"
              }
              `}
          >
            <ul className="relative ml-5 mt-1">
              {/* Tree Line */}
              <div
                className="
          absolute left-0 top-0 bottom-0
          w-[3px] rounded-full
          bg-gradient-to-b
          from-orange-400
          via-orange-500
          to-orange-600
          opacity-70
        "
              />

              {visibleSubItems.map((item, index) => {
                const childActive = location.pathname.startsWith(item.link);

                return (
                  <li
                    key={index}
                    className="relative ml-4"
                  >
                    {/* Connector */}
                    <span
                      className="
                absolute -left-4 top-1/2
                w-3 h-[2px]
                -translate-y-1/2
                bg-orange-500/50
              "
                    />

                    <Link
                      to={item.link}
                      onClick={() => setMobileOpen(false)}
                      className={`
                flex items-center gap-2
                px-3 py-2
                text-[12px]
                rounded-lg
                transition-all duration-200

                ${childActive
                          ? "bg-orange-50 text-orange-700 shadow-sm dark:bg-orange-500/10 dark:text-orange-400"
                          : "text-slate-500 hover:bg-orange-50 hover:text-orange-600 dark:text-slate-400 dark:hover:bg-slate-800"
                        }
              `}
                    >
                      {item.icon ? (
                        <span className="shrink-0 opacity-80">
                          {item.icon}
                        </span>
                      ) : (
                        <div className="w-1.5 h-1.5 rounded-full bg-current opacity-60" />
                      )}

                      <span className="truncate">
                        {item.text}
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        </>
      ) : (
        <>
          <Link
            to={link}
            onClick={() => setMobileOpen(false)}
            className={`
              flex items-center w-full
              ${expanded ? "gap-3 px-3" : "justify-center px-2"}
              py-2 rounded-md transition-all duration-200
              ${isActive
                ? "border-l-4 border-orange-500 bg-orange-100 text-orange-700 shadow-sm dark:bg-orange-500/15 dark:text-white"
                : "hover:bg-orange-100 hover:text-orange-700 dark:hover:bg-slate-800"
              }
            `}
          >
            {icon}

            <span
              className={`
                whitespace-nowrap overflow-hidden transition-all duration-200
                ${expanded ? "opacity-100 w-auto ml-1" : "opacity-0 w-0"}
              `}
            >
              {text}
            </span>
          </Link>

          {alert && (
            <span className="absolute right-2 top-2 w-2 h-2 bg-red-500 rounded-full" />
          )}
        </>
      )}

      {!expanded && (
        <span
          className="
            absolute left-full ml-6 px-2 py-2 rounded-md shadow-sm
            bg-orange-100 text-orange-600 text-sm
            invisible opacity-0 group-hover:visible group-hover:opacity-100
            transition-all duration-200
          "
        >
          {text}
        </span>
      )}
    </li>
  );
};